import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
	providedIn: 'root'
})
export class ScrollToTopService {
	private _document = inject(DOCUMENT);

	/**
	 * Scrolls to the top of the specified element or window.
	 * Respects user's reduced motion preference.
	 *
	 * @param element - Optional element to scroll. If not provided, scrolls the window.
	 * @param offset - Optional offset from the top in pixels (default: 0)
	 */
	scrollToTop(element?: Element | null, offset: number = 0): void {
		const prefersReducedMotion = this._document.defaultView?.matchMedia('(prefers-reduced-motion: reduce)')?.matches ?? false;

		const scrollBehavior: ScrollBehavior = prefersReducedMotion ? 'instant' : 'smooth';

		if (element && 'scrollTo' in element) {
			// Element has scrollTo method (e.g., HTMLElement)
			element.scrollTo({
				top: offset,
				behavior: scrollBehavior
			});
		} else if (element) {
			// Fallback for elements without scrollTo method
			const scrollableElement = element as HTMLElement;
			if (prefersReducedMotion) {
				scrollableElement.scrollTop = offset;
			} else {
				this._smoothScrollElement(element, offset);
			}
		} else {
			// Scroll the window
			this._document.defaultView?.scrollTo({
				top: offset,
				behavior: scrollBehavior
			});
		}
	}

	/**
	 * Finds the scrollable container for a given element by traversing up the DOM tree.
	 *
	 * @param element - The element to find the scrollable container for
	 * @returns The scrollable container element or null if not found
	 */
	findScrollableContainer(element: Element): Element | null {
		let parent = element.parentElement;

		while (parent && parent !== this._document.body) {
			const style = this._document.defaultView?.getComputedStyle(parent);
			if (style) {
				const overflow = style.overflow;
				const overflowY = style.overflowY;
				const overflowX = style.overflowX;

				if (
					overflow === 'auto' ||
					overflow === 'scroll' ||
					overflowY === 'auto' ||
					overflowY === 'scroll' ||
					overflowX === 'auto' ||
					overflowX === 'scroll'
				) {
					// Check if the element actually has scrollable content
					if (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth) {
						return parent;
					}
				}
			}
			parent = parent.parentElement;
		}

		return null;
	}

	/**
	 * Scrolls to the top of the table or list container.
	 * First tries to find a scrollable container, falls back to window scroll.
	 *
	 * @param tableElement - The table or list element
	 * @param offset - Optional offset from the top in pixels (default: 0)
	 */
	scrollTableToTop(tableElement: Element | null, offset: number = 0): void {
		if (!tableElement) {
			this.scrollToTop(null, offset);
			return;
		}

		const scrollableContainer = this.findScrollableContainer(tableElement);

		// Check if we should prefer window scrolling over container scrolling
		const shouldUseWindowScroll = this.shouldUseWindowScrolling(tableElement, scrollableContainer);

		if (scrollableContainer && !shouldUseWindowScroll) {
			this.scrollToTop(scrollableContainer, offset);
		} else {
			// Use scrollIntoView for more reliable positioning
			try {
				tableElement.scrollIntoView({
					behavior: this._document.defaultView?.matchMedia('(prefers-reduced-motion: reduce)')?.matches ? 'instant' : 'smooth',
					block: 'start',
					inline: 'nearest'
				});

				// Apply offset if specified
				if (offset > 0) {
					setTimeout(() => {
						const currentScrollY = this._document.defaultView?.pageYOffset ?? 0;
						this._document.defaultView?.scrollTo({
							top: Math.max(0, currentScrollY - offset),
							behavior: 'instant'
						});
					}, 50);
				}
			} catch (error) {
				console.error('ScrollService: scrollIntoView failed, using fallback:', error);
				// Fallback to original method if scrollIntoView fails
				const rect = tableElement.getBoundingClientRect();
				const scrollTop = this._document.defaultView?.pageYOffset ?? 0;
				const elementTop = rect.top + scrollTop - offset;

				this._document.defaultView?.scrollTo({
					top: Math.max(0, elementTop),
					behavior: this._document.defaultView?.matchMedia('(prefers-reduced-motion: reduce)')?.matches ? 'instant' : 'smooth'
				});
			}
		}
	}

	/**
	 * Determine if we should use window scrolling instead of container scrolling
	 */
	private shouldUseWindowScrolling(element: Element, scrollableContainer: Element | null): boolean {
		if (!scrollableContainer) return true;

		// Check if the scrollable container is the main application container
		// These containers typically manage the entire page layout and we want to scroll the window instead
		const containerTagName = scrollableContainer.tagName.toLowerCase();
		const containerClass = scrollableContainer.className.toLowerCase();

		const isMainAppContainer =
			containerTagName === 'lb-application-container' ||
			containerClass.includes('application-container') ||
			containerClass.includes('main-container') ||
			containerClass.includes('app-container');

		// For main app containers with grid/table/list elements, prefer window scrolling
		if (isMainAppContainer && this.isTableOrGridElement(element)) {
			return true;
		}

		// For other containers (like modals, sidebars, etc.), use container scrolling
		return false;
	}

	/**
	 * Check if an element is a table, grid, or list element that should use window scrolling
	 */
	private isTableOrGridElement(element: Element): boolean {
		const className = element.className.toLowerCase();
		const tagName = element.tagName.toLowerCase();

		return (
			tagName === 'table' ||
			className.includes('grid') ||
			className.includes('table') ||
			className.includes('list') ||
			element.querySelector('table, [class*="grid"], [class*="table"]') !== null
		);
	}

	/**
	 * Smooth scroll implementation for elements that don't support scrollTo with behavior
	 */
	private _smoothScrollElement(element: Element, targetScrollTop: number): void {
		const startScrollTop = element.scrollTop;
		const distance = targetScrollTop - startScrollTop;
		const duration = 300; // milliseconds
		let startTime: number | null = null;

		const animateScroll = (currentTime: number) => {
			if (startTime === null) startTime = currentTime;
			const timeElapsed = currentTime - startTime;
			const progress = Math.min(timeElapsed / duration, 1);

			// Easing function (ease-out)
			const easedProgress = 1 - Math.pow(1 - progress, 3);

			element.scrollTop = startScrollTop + distance * easedProgress;

			if (progress < 1) {
				requestAnimationFrame(animateScroll);
			}
		};

		requestAnimationFrame(animateScroll);
	}
}
